import { useTheme } from "next-themes";
import { Sun, Moon, Monitor, LogOut } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { UserPreferences } from "@/types/preferences";

const themeOptions = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
] as const;

export function SettingsPage() {
  const { theme, setTheme } = useTheme();
  const { user, signOut } = useAuth();
  const utils = trpc.useUtils();

  const { data: prefs } = trpc.settings.get.useQuery();
  const { data: configs } = trpc.scanConfigs.list.useQuery();

  const updateMutation = trpc.settings.update.useMutation({
    onMutate: async (newData) => {
      await utils.settings.get.cancel();
      const prev = utils.settings.get.getData();
      utils.settings.get.setData(undefined, (old) =>
        old ? { ...old, ...newData } : undefined
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.settings.get.setData(undefined, ctx.prev);
      toast.error("Failed to save settings");
    },
    onSettled: () => utils.settings.get.invalidate(),
  });

  const save = (partial: Partial<UserPreferences>) => {
    updateMutation.mutate(partial);
  };

  const handleThemeChange = (value: string) => {
    setTheme(value);
    save({ theme: value as "system" | "light" | "dark" });
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out successfully");
    } catch {
      toast.error("Failed to sign out");
    }
  };

  return (
    <div className="space-y-8 p-4 md:p-6 lg:p-8 max-w-3xl">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
        <p className="text-muted-foreground">
          Configure your account and preferences
        </p>
      </div>

      <div className="section-divider" />

      {/* Appearance */}
      <section className="space-y-4">
        <h2 className="text-xs font-medium uppercase tracking-widest text-muted-foreground/60">
          Appearance
        </h2>

        <div className="grid grid-cols-3 gap-3">
          {themeOptions.map(({ value, label, icon: Icon }) => (
            <Card
              key={value}
              onClick={() => handleThemeChange(value)}
              className={cn(
                "cursor-pointer p-4 flex flex-col items-center gap-2 transition-all",
                (theme ?? "system") === value
                  ? "border-accent ring-1 ring-accent/30"
                  : "hover:border-border-hover"
              )}
            >
              <Icon className="h-6 w-6 text-muted-foreground" />
              <span className="text-sm font-medium">{label}</span>
            </Card>
          ))}
        </div>
      </section>

      <div className="section-divider" />

      {/* Defaults */}
      <section className="space-y-4">
        <h2 className="text-xs font-medium uppercase tracking-widest text-muted-foreground/60">
          Defaults
        </h2>

        <Card className="p-5 space-y-5">
          {/* Default scan config */}
          <div className="space-y-2">
            <Label>Default Scan Config</Label>
            <Select
              value={prefs?.defaultConfigId ?? "none"}
              onValueChange={(v) =>
                save({ defaultConfigId: v === "none" ? null : v })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a config..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {configs?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Default map zoom */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Default Map Zoom</Label>
              <span className="text-sm text-muted-foreground tabular-nums">
                {prefs?.defaultMapZoom ?? 7}
              </span>
            </div>
            <Slider
              value={[prefs?.defaultMapZoom ?? 7]}
              min={1}
              max={20}
              step={1}
              onValueChange={([v]) => save({ defaultMapZoom: v })}
            />
          </div>

          {/* Default map center */}
          <div className="space-y-2">
            <Label>Default Map Center</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">
                  Latitude
                </Label>
                <Input
                  type="number"
                  step="0.0001"
                  placeholder="52.1326"
                  value={prefs?.defaultMapCenter?.lat ?? ""}
                  onChange={(e) => {
                    const lat = parseFloat(e.target.value);
                    if (isNaN(lat)) return;
                    save({
                      defaultMapCenter: {
                        lat,
                        lng: prefs?.defaultMapCenter?.lng ?? 5.2913,
                      },
                    });
                  }}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  Longitude
                </Label>
                <Input
                  type="number"
                  step="0.0001"
                  placeholder="5.2913"
                  value={prefs?.defaultMapCenter?.lng ?? ""}
                  onChange={(e) => {
                    const lng = parseFloat(e.target.value);
                    if (isNaN(lng)) return;
                    save({
                      defaultMapCenter: {
                        lat: prefs?.defaultMapCenter?.lat ?? 52.1326,
                        lng,
                      },
                    });
                  }}
                />
              </div>
            </div>
            {prefs?.defaultMapCenter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => save({ defaultMapCenter: null })}
              >
                Reset to default
              </Button>
            )}
          </div>
        </Card>
      </section>

      <div className="section-divider" />

      {/* Account */}
      <section className="space-y-4">
        <h2 className="text-xs font-medium uppercase tracking-widest text-muted-foreground/60">
          Account
        </h2>

        <Card className="p-5 space-y-4">
          <div className="space-y-1">
            <Label className="text-muted-foreground">Email</Label>
            <p className="text-sm font-medium">{user?.email ?? "—"}</p>
          </div>

          <Button variant="destructive" onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </Card>
      </section>
    </div>
  );
}

export default SettingsPage;
